import { useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import type { ModulePermissions } from "@/services/api/user/types";
import { useNotificationOptional } from "@/context/NotificationContext";
import { APP_REGISTRY, canAccessWindow } from "./OsAppRegistry";
import { useOsActionsOptional } from "./OsContext";
import { getGuardiansConversationWindowSize } from "@/components/guardians/conversation/openGuardiansConversation";
import { normalizeOsNavigateTarget, parseUrlToWindowConfig } from "./urlMapping";
import { preloadOsApp } from "./osAppPrefetch";

export function openUrlInOsContext(
  openWindow: ((config: {
    appId: string;
    title: string;
    params?: Record<string, string>;
    initialSize?: { width: number; height: number };
    newWindow?: boolean;
    replaceParams?: boolean;
  }) => void) | undefined,
  url: string,
  options?: {
    title?: string;
    newWindow?: boolean;
    replaceParams?: boolean;
    hasPermission?: (module: string) => boolean;
    user?: { company_id?: string; role?: string; permissions?: string[] } | null;
    modulePermissions?: Pick<ModulePermissions, "role" | "permissions"> | null;
    onAccessDenied?: () => void;
  },
): boolean {
  if (!openWindow || typeof window === "undefined") {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(normalizeOsNavigateTarget(url), window.location.origin);
  } catch {
    return false;
  }

  const windowConfig = parseUrlToWindowConfig(parsed.pathname, parsed.search);
  if (!windowConfig || !APP_REGISTRY[windowConfig.appId]) {
    return false;
  }

  const hasPermission = options?.hasPermission;
  if (
    hasPermission &&
    !canAccessWindow(
      windowConfig.appId,
      hasPermission,
      options.user,
      options.modulePermissions ?? null,
    )
  ) {
    options?.onAccessDenied?.();
    return true;
  }

  void preloadOsApp(windowConfig.appId);
  const appDef = APP_REGISTRY[windowConfig.appId];
  const initialSize =
    windowConfig.appId === "ops-guardians-conversation"
      ? getGuardiansConversationWindowSize()
      : appDef.defaultSize;

  openWindow({
    appId: windowConfig.appId,
    title: options?.title ?? windowConfig.title,
    params: windowConfig.params,
    initialSize,
    newWindow: options?.newWindow,
    replaceParams: options?.replaceParams,
  });
  return true;
}

export function useOpenUrl(): (
  url: string,
  options?: { title?: string; newWindow?: boolean; replaceParams?: boolean },
) => boolean {
  const actions = useOsActionsOptional();
  const { user } = useAuth();
  const { hasPermission, permissions } = useModulePermissions();
  const notification = useNotificationOptional();

  return useCallback(
    (url: string, options?: { title?: string; newWindow?: boolean; replaceParams?: boolean }) =>
      openUrlInOsContext(actions?.openWindow, url, {
        title: options?.title,
        newWindow: options?.newWindow,
        replaceParams: options?.replaceParams,
        hasPermission,
        user,
        modulePermissions: permissions,
        onAccessDenied: () => {
          notification?.showWarning(
            "Access denied",
            "You don't have permission to open this application.",
          );
        },
      }),
    [actions?.openWindow, hasPermission, notification, permissions, user],
  );
}
